import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { AlertCircle, CheckCircle2 } from "lucide-react";

export default function AdminResetPassword() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const token = sp.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-6">
        <div className="max-w-md w-full bg-[#F5F0E6] p-10 border border-[#E8DCC8] text-center">
          <AlertCircle size={40} className="mx-auto text-[#8A3A2B] mb-4" />
          <h1 className="font-serif text-3xl mb-4">Lien invalide</h1>
          <p className="text-sm text-[#4A4036] mb-6">Ce lien ne contient pas de jeton de réinitialisation.</p>
          <Link to="/admin/forgot-password" className="btn-primary">Demander un nouveau lien</Link>
        </div>
      </div>
    );
  }

  async function submit(e) {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError("Le mot de passe doit contenir au moins 8 caractères.");
    if (password !== confirm) return setError("Les deux mots de passe ne correspondent pas.");
    setLoading(true);
    try {
      await api.post("/admin/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => nav("/admin/login"), 2500);
    } catch (err) {
      setError(err.response?.data?.detail || "Une erreur est survenue.");
    } finally { setLoading(false); }
  }

  return (
    <div data-testid="admin-reset-page" className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-6">
      <div className="max-w-md w-full bg-[#F5F0E6] p-10 border border-[#E8DCC8]">
        <p className="font-hand text-2xl text-[#A3876A] mb-2">Back-office</p>
        <h1 className="font-serif text-4xl mb-4">Nouveau mot de passe</h1>

        {done ? (
          <div data-testid="admin-reset-done" className="mt-6">
            <div className="flex items-start gap-3 bg-[#EEF3EA] border border-[#C7D3BE] text-[#4C5F3F] px-4 py-4 text-sm leading-relaxed">
              <CheckCircle2 size={20} className="flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium mb-1">C'est fait ✨</p>
                <p>Ton mot de passe a été mis à jour. Tu vas être redirigée vers la connexion…</p>
              </div>
            </div>
          </div>
        ) : (
          <>
            <p className="text-sm text-[#4A4036] leading-relaxed mb-6">
              Choisis un mot de passe robuste — au moins 8 caractères.
            </p>
            {error && (
              <div
                data-testid="admin-reset-error"
                role="alert"
                className="flex items-start gap-3 bg-[#FBEBE8] border border-[#E4B8AF] text-[#8A3A2B] px-4 py-3 mb-6 text-sm leading-relaxed"
              >
                <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}
            <form onSubmit={submit} className="space-y-6">
              <input
                data-testid="admin-reset-password"
                className="field-input"
                type="password"
                placeholder="Nouveau mot de passe"
                required
                minLength={8}
                value={password}
                onChange={e => { setPassword(e.target.value); if (error) setError(""); }}
                autoComplete="new-password"
                autoFocus
              />
              <input
                data-testid="admin-reset-confirm"
                className="field-input"
                type="password"
                placeholder="Confirme le mot de passe"
                required
                minLength={8}
                value={confirm}
                onChange={e => { setConfirm(e.target.value); if (error) setError(""); }}
                autoComplete="new-password"
              />
              <button
                data-testid="admin-reset-submit"
                type="submit"
                disabled={loading}
                className="btn-primary w-full justify-center"
              >
                {loading ? "…" : "Mettre à jour"}
              </button>
            </form>
            <div className="mt-6 text-center">
              <Link to="/admin/login" className="text-sm text-[#A3876A] hover:text-[#8A7158] underline underline-offset-4">
                ← Retour à la connexion
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
